import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTodaySummary, updatePlannerStatus } from "./TodayService";
import { apiFetch } from "../api";
import { BASE_URL } from "../../commonUrls";
import "./Today.css";

const VIEW_KEY = "lifeos-view-mode";
const VIEW_OPTIONS = [
  { label: "Dark",      value: "dark" },
  { label: "Light",     value: "light" },
  { label: "Developer", value: "developer" },
];

export default function Today() {
  const nav = useNavigate();
  const [summary, setSummary] = useState({ urgent: [], reminders: [], payments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeDays, setRangeDays] = useState(15);
  const [updatingId, setUpdatingId] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [viewMode, setViewMode] = useState("dark");
  const [aiPriorities, setAiPriorities] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    if (stored && VIEW_OPTIONS.some(o => o.value === stored)) setViewMode(stored);
  }, []);

  useEffect(() => {
    document.body.dataset.viewMode = viewMode;
    localStorage.setItem(VIEW_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getTodaySummary(rangeDays)
      .then((data) => {
        if (!alive) return;
        setSummary({
          urgent:    data?.urgent    || [],
          reminders: data?.reminders || [],
          payments:  data?.payments  || [],
        });
      })
      .catch(() => { if (alive) setError("Unable to load today summary."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [rangeDays]);

  const formatDate = (d) => {
    if (!d) return "No date";
    return new Date(d).toLocaleDateString();
  };

  const splitByDate = (items, dateKey) => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const passed = [];
    const upcoming = [];
    items.forEach((item) => {
      const raw = item[dateKey];
      if (!raw) { upcoming.push(item); return; }
      new Date(raw) < startOfToday ? passed.push(item) : upcoming.push(item);
    });
    return { passed, upcoming };
  };

  const counts = useMemo(() => ({
    urgent:    summary.urgent.length,
    reminders: summary.reminders.length,
    payments:  summary.payments.length,
  }), [summary]);

  const handleStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await updatePlannerStatus(id, status);
      setSummary((prev) => ({
        urgent:    prev.urgent.filter(i => i.id !== id),
        reminders: prev.reminders.filter(i => i.id !== id),
        payments:  prev.payments.filter(i => i.id !== id),
      }));
      setToast({ message: `Marked as ${status.toLowerCase()}.`, type: "success" });
    } catch {
      setToast({ message: "Update failed. Try again.", type: "error" });
    } finally {
      setUpdatingId("");
    }
  };

  useEffect(() => {
    if (!toast.message) return undefined;
    const t = setTimeout(() => setToast({ message: "", type: "" }), 2500);
    return () => clearTimeout(t);
  }, [toast.message]);

  const handleAiPriorities = async () => {
    setAiLoading(true);
    setAiPriorities("");
    const allItems = [...summary.urgent, ...summary.reminders];
    if (!allItems.length) { setAiPriorities("No tasks to prioritize."); setAiLoading(false); return; }
    const list = allItems.map((i, idx) =>
      `${idx + 1}. ${i.title}${i.dueDate ? " (due " + i.dueDate + ")" : ""}${i.priority ? " [priority " + i.priority + "]" : ""}`
    ).join("\n");
    const prompt = `You are a personal productivity assistant. Given these tasks, suggest the top 3 to focus on today and briefly explain why (1 sentence each):\n\n${list}\n\nReply in plain text, no markdown.`;
    try {
      const res = await apiFetch(`${BASE_URL}/generate-text?provider=OPENAI`, {
        method: "POST",
        body: prompt,
        headers: { "Content-Type": "text/plain" },
      });
      const text = res.ok ? await res.text() : "AI service unavailable.";
      setAiPriorities(text);
    } catch {
      setAiPriorities("Could not reach AI service.");
    }
    setAiLoading(false);
  };

  const rangeOptions = [
    { label: "Today",  value: 0 },
    { label: "7 days", value: 7 },
    { label: "15 days", value: 15 },
  ];

  return (
    <div className="today-page">
      {toast.message && (
        <div className={`today-toast today-toast-${toast.type}`}>{toast.message}</div>
      )}

      {/* TOP BAR */}
      <div className="today-topbar">
        <div className="today-topbar-left">
          <h1 className="today-title">Today</h1>
          <div className="today-range">
            {rangeOptions.map((opt) => (
              <button
                key={opt.value}
                className={`today-range-btn${rangeDays === opt.value ? " active" : ""}`}
                onClick={() => setRangeDays(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="today-view-switch" role="radiogroup" aria-label="View mode">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={viewMode === opt.value}
              className={`today-view-btn${viewMode === opt.value ? " active" : ""}`}
              onClick={() => setViewMode(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI BAR */}
      <div className="today-ai-bar">
        <button className="today-ai-btn" onClick={handleAiPriorities} disabled={aiLoading}>
          {aiLoading ? "Thinking…" : "✨ Smart Priorities"}
        </button>
        <button className="today-ai-btn today-ai-btn-ghost" onClick={() => nav("/search?mode=assistant")}>
          + Quick Add
        </button>
      </div>

      {aiPriorities && (
        <div className="today-ai-result">
          <button className="today-ai-close" onClick={() => setAiPriorities("")}>×</button>
          <pre className="today-ai-text">{aiPriorities}</pre>
        </div>
      )}

      {/* CONTENT GRID */}
      <div className="today-grid">
        <TaskCard
          title="Urgent"
          count={counts.urgent}
          accent="red"
          loading={loading}
          error={error}
          empty={summary.urgent.length === 0}
          emptyMsg="You're clear."
          onAddClick={() => nav("/planner")}
        >
          <ItemSection
            data={splitByDate(summary.urgent, "dueDate")}
            renderItem={(item) => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[item.type, formatDate(item.dueDate)]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        <TaskCard
          title="Reminders"
          count={counts.reminders}
          accent="amber"
          loading={loading}
          error={error}
          empty={summary.reminders.length === 0}
          emptyMsg="No reminders in this range."
          onAddClick={() => nav("/planner")}
        >
          <ItemSection
            data={splitByDate(summary.reminders, "dueDate")}
            renderItem={(item) => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[item.type, formatDate(item.dueDate)]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        <TaskCard
          title="Payments"
          count={counts.payments}
          accent="green"
          loading={loading}
          error={error}
          empty={summary.payments.length === 0}
          emptyMsg="No payments due."
          onAddClick={() => nav("/planner")}
        >
          <ItemSection
            data={splitByDate(summary.payments, "paymentDate")}
            renderItem={(item) => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[item.paymentType, formatDate(item.paymentDate), item.amount != null ? `₹${item.amount}` : null]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        {/* QUICK ACTIONS */}
        <div className="today-card today-card-actions">
          <div className="today-card-head">
            <span className="today-card-title">Quick Actions</span>
          </div>
          <div className="today-actions-grid">
            {[
              { label: "Planner",    emoji: "🗓",  path: "/planner" },
              { label: "Next Day",   emoji: "🧭",  path: "/planner/next-day" },
              { label: "Finance",    emoji: "💰",  path: "/finance" },
              { label: "Calendar",   emoji: "📅",  path: "/calendar" },
              { label: "Journal",    emoji: "📓",  path: "/journal" },
              { label: "Notes",      emoji: "📝",  path: "/notes" },
              { label: "Files",      emoji: "📁",  path: "/files" },
            ].map(({ label, emoji, path }) => (
              <button key={path} className="today-action-btn" onClick={() => nav(path)}>
                <span className="today-action-emoji">{emoji}</span>
                <span className="today-action-label">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskCard({ title, count, accent, loading, error, empty, emptyMsg, onAddClick, children }) {
  return (
    <div className={`today-card today-card-${accent}`}>
      <div className="today-card-head">
        <span className="today-card-title">{title}</span>
        <span className={`today-card-count today-count-${accent}`}>{count}</span>
      </div>
      {loading ? (
        <p className="today-muted">Loading…</p>
      ) : error ? (
        <p className="today-muted today-error">{error}</p>
      ) : empty ? (
        <div className="today-empty">
          <span className="today-empty-msg">{emptyMsg}</span>
          <button className="today-empty-btn" onClick={onAddClick}>+ Add</button>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function ItemSection({ data, renderItem }) {
  return (
    <div className="today-items">
      {data.passed.length > 0 && (
        <>
          <div className="today-items-label">Overdue</div>
          {data.passed.map(item => renderItem(item))}
        </>
      )}
      {data.upcoming.length > 0 && (
        <>
          {data.passed.length > 0 && <div className="today-items-label">Upcoming</div>}
          {data.upcoming.map(item => renderItem(item))}
        </>
      )}
    </div>
  );
}

function TodayRow({ title, meta, disabled, onDone, onSkip }) {
  return (
    <div className="today-row">
      <div className="today-row-body">
        <div className="today-row-title">{title}</div>
        <div className="today-row-meta">
          {meta.filter(Boolean).map((m, idx) => (
            <span key={idx} className="today-row-tag">{m}</span>
          ))}
        </div>
      </div>
      <div className="today-row-actions">
        <button className="today-row-btn today-btn-done" onClick={onDone} disabled={disabled}>
          Done
        </button>
        <button className="today-row-btn today-btn-skip" onClick={onSkip} disabled={disabled}>
          Skip
        </button>
      </div>
    </div>
  );
}
